import React, { useCallback, useState } from "react"
import { dayjs } from "src/lib/dayjs"
import {
  CREDIT_CONTRACT_PREPARATION_STATUS_CONFIG,
  CreditContractPreparationStatus,
  CreditContractPreparationTableRowProps,
  EXAMINATION_STATUS_CONFIG,
} from "../types"
import {
  useTriggerCreditContractPreparationAutoExtraction,
  useUpdateCreditContractPreparationStatus,
} from "../hooks/useCreditContractPreparations"
import Select from "src/components/tailwind-components/Select"
import { useNotification } from "@smoothincorg/smooth-ui"

const TWO_DAYS_FROM_NOW = dayjs().add(2, "day")
const STATUS_OPTIONS = Object.entries(CREDIT_CONTRACT_PREPARATION_STATUS_CONFIG).map(
  ([value, config]) => ({
    value,
    label: config.label,
  })
)

export const CreditContractPreparationTableRow: React.FC<
  CreditContractPreparationTableRowProps
> = ({ item, onStatusUpdate }) => {
  const [status, setStatus] = useState<CreditContractPreparationStatus>(item.status)
  const { updateStatus, isUpdating } = useUpdateCreditContractPreparationStatus()
  const { triggerAutoExtraction, isTriggering } =
    useTriggerCreditContractPreparationAutoExtraction()
  const { showSnackbar } = useNotification()

  const handleClick = () => {
    // 新しいタブでナビゲーション実行
    const newWindow = window.open("/admin/lifftter/" + item.proposalId, "_blank")
    if (newWindow) {
      newWindow.opener = null // セキュリティ対策
    }
  }

  // 日付フォーマット（日本語曜日を含む）
  const formatDate = (dateString: string) => {
    return dayjs(dateString).format("YYYY/MM/DD (ddd)")
  }

  // 最終更新日時フォーマット（時間を含む）
  const formatDateTime = (dateString: string) => {
    return dayjs(dateString).format("YYYY/MM/DD (ddd) HH:mm:ss")
  }

  // 着金日が現在時刻の2日後以前（2日後を含む）かチェック
  const isOverdue =
    item.paymentInitialCostOn && !dayjs(item.paymentInitialCostOn).isAfter(TWO_DAYS_FROM_NOW, "day")

  // 行のクリック発火を抑止する
  const handleSelectClick = useCallback((event: React.MouseEvent) => {
    event.stopPropagation()
  }, [])

  // 審査状況のバッジ設定を取得
  const statusConfig = EXAMINATION_STATUS_CONFIG[item.examinationStatus]

  const handleStatusChange = useCallback(
    async (value: string) => {
      const nextStatus = value as CreditContractPreparationStatus
      if (!nextStatus || nextStatus === status) return

      const previousStatus = status

      try {
        setStatus(nextStatus)
        await updateStatus(item.id, nextStatus)
        showSnackbar("ステータスを更新しました", "success")

        // キャッシュを無効化
        if (onStatusUpdate) {
          await onStatusUpdate()
        }
      } catch (error) {
        console.error("ステータスの更新に失敗しました:", error)
        setStatus(previousStatus)
        showSnackbar("ステータスの更新に失敗しました", "error")
      }
    },
    [item.id, status, updateStatus, showSnackbar, onStatusUpdate]
  )

  const handleAutoExtractionTrigger = useCallback(
    async (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation()

      try {
        await triggerAutoExtraction(item.id)
        showSnackbar("自動抽出をキューに登録しました", "success")

        if (onStatusUpdate) {
          await onStatusUpdate()
        }
      } catch (error) {
        showSnackbar("自動抽出のキュー登録に失敗しました", "error")
      }
    },
    [triggerAutoExtraction, item.id, showSnackbar, onStatusUpdate]
  )

  const getAutoExtractionStatus = () => {
    const autoExtraction = item.autoExtraction

    if (!autoExtraction) {
      return {
        label: "未実行",
        className: "bg-gray-100 text-gray-700 border-gray-200",
      }
    }

    const { documentExtractions } = autoExtraction
    if (!documentExtractions || documentExtractions.length === 0) {
      return {
        label: autoExtraction.readyAt ? "キュー登録済み" : "未実行",
        className: autoExtraction.readyAt
          ? "bg-blue-100 text-blue-700 border-blue-200"
          : "bg-gray-100 text-gray-700 border-gray-200",
      }
    }

    const statuses = documentExtractions.map((extraction) => extraction.status)

    if (statuses.some((status) => status === "processing" || status === "pending")) {
      return {
        label: "処理中",
        className: "bg-blue-100 text-blue-700 border-blue-200",
      }
    }

    const hasSucceeded = statuses.some((status) => status === "succeeded")
    const hasFailed = statuses.some((status) => status === "failed")

    if (statuses.every((status) => status === "succeeded")) {
      return {
        label: "完了",
        className: "bg-green-100 text-green-700 border-green-200",
      }
    }

    if (hasSucceeded && hasFailed) {
      return {
        label: "一部成功",
        className: "bg-yellow-100 text-yellow-700 border-yellow-200",
      }
    }

    if (hasFailed) {
      return {
        label: "失敗",
        className: "bg-red-100 text-red-700 border-red-200",
      }
    }

    return {
      label: "不明",
      className: "bg-gray-100 text-gray-700 border-gray-200",
    }
  }

  const autoExtractionStatus = getAutoExtractionStatus()

  const latestAutoExtractionUpdatedAt = item.autoExtraction?.documentExtractions
    ?.map((extraction) => extraction.updatedAt)
    .sort()
    .pop()

  const autoExtractionTimestamp =
    latestAutoExtractionUpdatedAt || item.autoExtraction?.readyAt || null

  // Check if there's any previous auto extraction trace
  const hasPreviousAutoExtractionTrace =
    item.autoExtraction &&
    (item.autoExtraction.readyAt ||
      (item.autoExtraction.documentExtractions &&
        item.autoExtraction.documentExtractions.length > 0))

  return (
    <tr
      className="cursor-pointer border-b border-gray-200 transition-colors hover:bg-gray-50"
      onClick={handleClick}
    >
      {/* ユーザーID */}
      <td className="px-4 py-5 text-center align-middle text-[14px] font-medium text-gray-900">
        {item.userId}
      </td>

      {/* ユーザー氏名 */}
      <td className="px-4 py-5 text-center align-middle text-[14px] text-gray-900">
        {item.userName}
      </td>

      {/* 着金日 */}
      <td className="px-4 py-5 text-center align-middle text-[14px]">
        <span
          className={
            isOverdue
              ? "font-medium text-red-600"
              : item.paymentInitialCostOn
              ? "text-gray-900"
              : "text-gray-500"
          }
        >
          {formatDate(item.paymentInitialCostOn)}
        </span>
      </td>

      {/* 加盟店名 */}
      <td className="px-4 py-5 text-center align-middle text-[14px] text-gray-900">
        {item.storeName || ""}
      </td>

      {/* 店舗の定休日 */}
      <td className="px-4 py-5 text-center align-middle text-[14px] text-gray-900">
        <div className="whitespace-pre-line store-holiday">
          {item.storeHoliday?.split(" ").join("\n") || ""}
        </div>
      </td>

      {/* 審査状況 */}
      <td className="px-4 py-5 text-center align-middle">
        <span
          className={`inline-flex items-center rounded-full border px-3.5 py-1.5 text-[14px] font-medium ${statusConfig.className}`}
        >
          {statusConfig.label}
        </span>
      </td>

      {/* 立替申請日 */}
      <td className="px-4 py-5 text-center align-middle text-[14px] text-gray-900">
        {formatDate(item.createdAt)}
      </td>

      {/* メモ */}
      <td className="max-w-xs px-4 py-5 text-[14px] text-gray-900">
        <div className="whitespace-normal break-words">{item.memo}</div>
      </td>

      {/* 担当者 */}
      <td className="px-4 py-5 text-center align-middle text-[14px] text-gray-900">
        {item.smootherName || "なし"}
      </td>

      {/* 最終更新 */}
      <td className="px-4 py-5 text-center align-middle text-[14px] text-gray-500">
        {formatDateTime(item.updatedAt)}
      </td>

      {/* ステータス */}
      <td className="px-4 py-5 text-center align-middle text-[14px] text-gray-900">
        <div onClick={handleSelectClick}>
          <Select
            options={STATUS_OPTIONS}
            value={status}
            onChange={(e) => handleStatusChange(e.target.value)}
            disabled={isUpdating}
            showPlaceholder={false}
            className="mx-auto w-[150px] text-center"
          />
        </div>
      </td>

      {/* 自動抽出 */}
      <td className="px-4 py-5 text-center align-middle text-[14px] text-gray-900">
        <div className="flex flex-col items-center gap-2" onClick={handleSelectClick}>
          <span
            className={`inline-flex items-center rounded-full border px-3.5 py-1.5 text-[13px] font-medium ${autoExtractionStatus.className}`}
          >
            {autoExtractionStatus.label}
          </span>
          {autoExtractionTimestamp ? (
            <span className="text-[12px] text-gray-500">
              最終更新: {formatDateTime(autoExtractionTimestamp)}
            </span>
          ) : null}
          <button
            type="button"
            className="rounded-md border border-indigo-500 px-3 py-1 text-[13px] font-medium text-indigo-600 transition hover:bg-indigo-50 disabled:cursor-not-allowed disabled:border-gray-300 disabled:text-gray-400 disabled:hover:bg-transparent"
            onClick={handleAutoExtractionTrigger}
            disabled={isTriggering}
          >
            {isTriggering
              ? "キュー登録中..."
              : hasPreviousAutoExtractionTrace
              ? "自動抽出を再実行"
              : "自動抽出を開始"}
          </button>
        </div>
      </td>
    </tr>
  )
}
